/**
 * App-level confirmation toast ("… · View ›") driven by a sequence counter.
 *
 * Mounted ONCE high in the tree (App.tsx) so it outlives the translation popup /
 * word panel, which unmount on close — a toast rendered inside them would vanish
 * immediately. The relevant flow bumps a counter observable and this shows the
 * toast for 3s. Renders nothing until the first bump. Both the saved-phrase and
 * known-word confirmations are instances of this.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { observer } from "@legendapp/state/react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ChevronRight } from "lucide-react";
import type { Observable } from "@legendapp/state";
import { colors } from "@/ui";
import { handlePanelClose } from "../../state/videoState";

const VISIBLE_MS = 3000;

interface SequenceToastProps {
  /** Counter bumped each time the toast should show (0 = never shown yet). */
  seq$: Observable<number>;
  message: string;
  viewTo: string;
}

export const SequenceToast = observer(function SequenceToast({
  seq$,
  message,
  viewTo,
}: SequenceToastProps) {
  const seq = seq$.get();
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (seq === 0) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), VISIBLE_MS);
    return () => clearTimeout(t);
  }, [seq]);

  if (!visible) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: "calc(96px + env(safe-area-inset-bottom))",
        zIndex: 60,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        padding: "0 16px",
      }}
    >
      <div
        className="flex items-center gap-3"
        style={{
          pointerEvents: "auto",
          background: "rgba(28,28,30,0.96)",
          color: "#fff",
          borderRadius: 16,
          padding: "12px 16px",
          maxWidth: 420,
          width: "100%",
          boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
        }}
      >
        <CheckCircle2 size={22} color="#fff" fill={colors.green} strokeWidth={2} />
        <span className="flex-1 text-sm font-semibold">{message}</span>
        <button
          type="button"
          className="inline-flex items-center text-sm font-semibold"
          style={{ color: "#fff" }}
          onClick={() => {
            setVisible(false);
            // Clear the lingering selection + unpause before leaving the feed.
            handlePanelClose();
            navigate(viewTo);
          }}
        >
          View
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>,
    document.body,
  );
});
